import React, { useMemo, useState, useEffect } from 'react';
import { apiClient } from '../../services/apiClient';
import { DataListTable, type Column } from '../../components/crm/DataListTable';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'user';
  is_active: boolean;
  created_at?: string;
  date_joined?: string;
  tenant?: {
    id: string;
    name: string;
    subdomain: string;
  };
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'admin' | 'user'>('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiClient.get('/users/');
      const list = Array.isArray(data) ? data : (data?.results ?? []);
      setUsers(list);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUserStatus = async (userId: string, isActive: boolean) => {
    try {
      setError('');
      await apiClient.patch(`/users/${userId}/`, { is_active: !isActive });
      await fetchUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to update user status');
    }
  };

  const handleChangeUserRole = async (userId: string, newRole: 'admin' | 'user') => {
    if (!window.confirm(`Are you sure you want to change this user's role to ${newRole}?`)) {
      return;
    }

    try {
      setError('');
      await apiClient.patch(`/users/${userId}/`, { role: newRole });
      await fetchUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to update user role');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.last_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const columns = useMemo<Column<User>[]>(
    () => [
      {
        id: 'user',
        header: 'User',
        mobilePriority: 'primary',
        mobileLabel: 'User',
        render: (user) => (
          <div>
            <div className="text-sm font-medium text-gray-900">
              {user.first_name} {user.last_name}
            </div>
            <div className="text-sm text-gray-500">{user.email}</div>
          </div>
        ),
      },
      {
        id: 'tenant',
        header: 'Tenant',
        mobileLabel: 'Tenant',
        render: (user) =>
          user.tenant ? (
            <div>
              <div className="text-sm text-gray-900">{user.tenant.name}</div>
              <div className="text-sm text-gray-500">{user.tenant.subdomain}</div>
            </div>
          ) : (
            <span className="text-sm text-gray-400">No tenant</span>
          ),
      },
      {
        id: 'role',
        header: 'Role',
        mobileLabel: 'Role',
        render: (user) => (
          <select
            value={user.role}
            onChange={(e) => handleChangeUserRole(user.id, e.target.value as 'admin' | 'user')}
            className={`text-xs font-semibold px-3 py-1 rounded-full border ${
              user.role === 'admin'
                ? 'bg-purple-100 text-purple-800 border-purple-200'
                : 'bg-blue-100 text-blue-800 border-blue-200'
            }`}
          >
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        mobileLabel: 'Status',
        render: (user) => (
          <button
            onClick={() => handleToggleUserStatus(user.id, user.is_active)}
            className={`px-3 py-1 text-xs font-semibold rounded-full border ${
              user.is_active
                ? 'bg-green-100 text-green-800 border-green-200'
                : 'bg-gray-100 text-gray-800 border-gray-200'
            }`}
          >
            {user.is_active ? 'Active' : 'Inactive'}
          </button>
        ),
      },
      {
        id: 'joined',
        header: 'Joined',
        mobileLabel: 'Joined',
        render: (user) => (
          <span className="text-sm text-gray-500">
            {formatDate(user.created_at ?? user.date_joined ?? '')}
          </span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        mobileLabel: 'Actions',
        render: (user) => (
          <div data-row-action>
            <button
              onClick={() => setSelectedUser(user)}
              className="text-primary hover:text-indigo-900 text-sm font-medium"
            >
              View Details
            </button>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">User Management</h2>

        {/* Filters */}
        <div className="flex gap-4">
          <div className="flex-1">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value as 'all' | 'admin' | 'user')}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">All Roles</option>
            <option value="admin">Admins</option>
            <option value="user">Users</option>
          </select>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <DataListTable<User>
        columns={columns}
        rows={filteredUsers}
        getRowKey={(user) => user.id}
        loading={loading}
        emptyContent={
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <p className="text-gray-500">No users found.</p>
          </div>
        }
      />

      {/* User Detail Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">User Details</h3>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                    <p className="text-gray-900">{selectedUser.first_name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                    <p className="text-gray-900">{selectedUser.last_name}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <p className="text-gray-900">{selectedUser.email}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <span className={`px-3 py-1 text-sm font-semibold rounded-full border ${
                    selectedUser.role === 'admin'
                      ? 'bg-purple-100 text-purple-800 border-purple-200'
                      : 'bg-blue-100 text-blue-800 border-blue-200'
                  }`}>
                    {selectedUser.role}
                  </span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <span className={`px-3 py-1 text-sm font-semibold rounded-full border ${
                    selectedUser.is_active
                      ? 'bg-green-100 text-green-800 border-green-200'
                      : 'bg-gray-100 text-gray-800 border-gray-200'
                  }`}>
                    {selectedUser.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {selectedUser.tenant && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tenant</label>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-gray-900 font-medium">{selectedUser.tenant.name}</p>
                      <p className="text-sm text-gray-500">Subdomain: {selectedUser.tenant.subdomain}</p>
                      <p className="text-sm text-gray-500">ID: {selectedUser.tenant.id}</p>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Joined Date</label>
                  <p className="text-gray-900">{formatDate(selectedUser.created_at ?? selectedUser.date_joined ?? '')}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">User ID</label>
                  <p className="text-gray-600 text-sm font-mono">{selectedUser.id}</p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => handleToggleUserStatus(selectedUser.id, selectedUser.is_active)}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium ${
                    selectedUser.is_active
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {selectedUser.is_active ? 'Deactivate User' : 'Activate User'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
